import ResourceModule, { type ResourceModuleConfig } from "@/pages/admin/components/ResourceModule";
import { type Column } from "@/pages/admin/components/AdminUI";
import { SETTING_GROUPS, SETTING_TYPES, type SystemSetting } from "@/lib/adminSystem";

const options = (list: string[]) => list.map((value) => ({ value, label: value }));

const columns: Column<SystemSetting>[] = [
  {
    key: "label",
    header: "Setting",
    primary: true,
    render: (row) => (
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.label}</p>
        <p className="text-xs text-foreground-500 mt-0.5">{row.key}</p>
      </div>
    ),
  },
  { key: "group", header: "Group", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.group_name}</span> },
  { key: "value", header: "Value", render: (row) => <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{row.value || "—"}</span> },
  { key: "type", header: "Type", render: (row) => <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full whitespace-nowrap">{row.value_type}</span> },
];

const config: ResourceModuleConfig<SystemSetting> = {
  table: "system_settings",
  orderBy: "group_name",
  eyebrow: "Administration",
  title: "System Settings",
  subtitle: "The platform's core configuration — identity, commerce, operations and notification defaults used across the site.",
  searchPlaceholder: "Search settings…",
  searchKeys: ["key", "label", "group_name", "value", "description"],
  filters: [{ key: "group_name", label: "Group", options: options(SETTING_GROUPS) }],
  statusMap: undefined,
  stats: (rows) => [
    { label: "Settings", value: rows.length, icon: "ri-settings-3-line", tone: "primary" },
    { label: "Groups", value: new Set(rows.map((row) => row.group_name)).size, icon: "ri-stack-line", tone: "secondary" },
    { label: "Commerce", value: rows.filter((row) => row.group_name === "Commerce").length, icon: "ri-money-rupee-circle-line", tone: "accent" },
    { label: "Operations", value: rows.filter((row) => row.group_name === "Operations").length, icon: "ri-tools-line", tone: "secondary" },
  ],
  columns,
  fields: [
    { key: "label", label: "Label" },
    { key: "key", label: "Key" },
    { key: "group_name", label: "Group", type: "select", options: options(SETTING_GROUPS) },
    { key: "value_type", label: "Type", type: "select", options: options(SETTING_TYPES) },
    { key: "value", label: "Value" },
    { key: "description", label: "Description", type: "textarea" },
  ],
  createFields: [
    { key: "label", label: "Label" },
    { key: "key", label: "Key", placeholder: "e.g. booking_lead_days" },
    { key: "group_name", label: "Group", type: "select", options: options(SETTING_GROUPS) },
    { key: "value_type", label: "Type", type: "select", options: options(SETTING_TYPES) },
    { key: "value", label: "Value" },
    { key: "description", label: "Description", type: "textarea" },
  ],
  newDefaults: {
    key: "",
    label: "",
    group_name: "General",
    value: "",
    value_type: "text",
    description: "",
  },
  emptyTitle: "No settings yet",
  emptyMessage: "Add configuration keys that the platform and reports use at runtime.",
  rowTitle: (row) => row.label,
};

export default function AdminSystemSettings() {
  return <ResourceModule config={config} />;
}